import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import {
  PropertiesReportRow,
  PropertiesReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';

@Component({
  selector: 'app-property-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass
  ],
  templateUrl: './property-reports.component.html',
  styleUrl: './property-reports.component.css'
})
export class PropertyReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Properties',
    breadcrumb_url: '/admin/reports/properties'
  };

  activeTab: 'all' | 'by-status' | 'by-city' | 'by-category' | 'graphs' = 'all';

  page: number = 1;
  totalRows: number = 50;
  totalPage: number = 0;
  pageCount: number = 1;

  reportData: PropertiesReportRow[] = [];
  summaryData: PropertiesReportSummary | null = null;

  filterForm!: FormGroup;
  disableClear: boolean = false;

  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  searchInput$ = new Subject<void>();

  statusOptions = [
    { value: 'Work in Progress', label: 'Work in Progress' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Archived', label: 'Archived' },
    { value: 'Enquiry', label: 'Enquiry' }
  ];

  categoryOptions = [
    { value: 'End-to-End Management', label: 'End-to-End Management' },
    { value: 'Sale', label: 'Sale' },
    { value: 'Property Management', label: 'Property Management' },
    { value: 'Tenant Placement', label: 'Tenant Placement' },
    { value: 'Plot Monitoring', label: 'Plot Monitoring' },
    { value: 'Repairs & Renovation', label: 'Repairs & Renovation' },
    { value: 'Documentation', label: 'Documentation' }
  ];

  listingStatusOptions = [
    { value: 'Sell', label: 'Sell' },
    { value: 'Sold', label: 'Sold' },
    { value: 'Rent Available', label: 'Rent Available' },
    { value: 'Rent Occupied', label: 'Rent Occupied' },
    { value: 'Property Management', label: 'Property Management' },
    { value: 'Tenant Placement', label: 'Tenant Placement' },
    { value: 'Plot Monitoring', label: 'Plot Monitoring' },
    { value: 'Repairs & Renovation', label: 'Repairs & Renovation' },
    { value: 'Documentation', label: 'Documentation' }
  ];

  hasOwnerOptions = [
    { value: 'true', label: 'With Owner' },
    { value: 'false', label: 'Without Owner' }
  ];

  idProofOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  private reportSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;
  private searchDebounceSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupManagerSearch();
    this.setupSearchDebounce();
    this.loadReport();
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      search: [''],
      city: [''],
      status: ['Approved'],
      managed_by: [[]],
      category: [[]],
      listing_status: [null],
      has_owner: [null],
      id_proof_attached: [null]
    });
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  private setupSearchDebounce(): void {
    this.searchDebounceSubscription = this.searchInput$
      .pipe(debounceTime(400))
      .subscribe(() => {
        this.disableClear = true;
        this.page = 1;
        this.loadReport();
      });
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onManagerScrollEnd(): void {
    if (!this.hasMoreManagers) return;
    this.managerPage++;
    this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.managersList = [...this.managersList, ...more];
        this.hasMoreManagers = this.managersList.length < (resp.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });
      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  switchTab(tab: 'all' | 'by-status' | 'by-city' | 'by-category' | 'graphs'): void {
    this.activeTab = tab;
  }

  /** The Manager filter is multi-select; normalise whatever ng-select hands back to a uid list. */
  private managerIdsFromValue(value: any): string[] {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
      .map((v: any) => (v && typeof v === 'object' ? v.value : v))
      .filter((v: any) => !!v);
  }

  private buildFilterQuery(): string {
    let query = '';
    const v = this.filterForm.getRawValue();
    if (v.search) query += `&search=${encodeURIComponent(v.search)}`;
    if (v.city) query += `&city=${encodeURIComponent(v.city)}`;
    if (v.status) query += `&status=${encodeURIComponent(v.status)}`;
    const managerIds = this.managerIdsFromValue(v.managed_by);
    if (managerIds.length) query += `&managed_by=${managerIds.join(',')}`;
    if (Array.isArray(v.category) && v.category.length) {
      query += `&category=${encodeURIComponent(v.category.join(','))}`;
    } else if (typeof v.category === 'string' && v.category) {
      query += `&category=${encodeURIComponent(v.category)}`;
    }
    if (v.listing_status) query += `&listing_status=${encodeURIComponent(v.listing_status)}`;
    if (v.has_owner) query += `&has_owner=${v.has_owner}`;
    if (v.id_proof_attached) query += `&id_proof_attached=${v.id_proof_attached}`;
    return query;
  }

  loadReport(): void {
    this.reportSubscription?.unsubscribe();
    const query = this.buildFilterQuery();
    this.reportSubscription = this.httpService
      .getPropertiesReportAll(this.page, this.totalRows, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.reportData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPage = response.data.total ?? 0;
            this.pageCount = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load properties report', 'toast-top-right');
        }
      });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.page = 1;
    this.loadReport();
  }

  onTextFilterChange(): void {
    this.disableClear = true;
    this.searchInput$.next();
  }

  resetFilters(): void {
    this.filterForm.reset({
      search: '', city: '', status: 'Approved', managed_by: [],
      category: [], listing_status: null, has_owner: null, id_proof_attached: null
    });
    this.disableClear = false;
    this.page = 1;
    this.loadReport();
  }

  // Quick-stat cards under the filters: one per property category (same
  // colours as the properties list). A property can tick several categories.
  categoryCards: { key: string; bgClass: string }[] = [
    { key: 'End-to-End Management', bgClass: 'bg-info text-white' },
    { key: 'Sale', bgClass: 'bg-primary text-white' },
    { key: 'Property Management', bgClass: 'bg-success text-white' },
    { key: 'Tenant Placement', bgClass: 'bg-warning text-dark' },
    { key: 'Plot Monitoring', bgClass: 'bg-danger text-white' },
    { key: 'Repairs & Renovation', bgClass: 'bg-secondary text-white' },
    { key: 'Documentation', bgClass: 'bg-dark text-white' }
  ];

  getCategoryCount(category: string): number {
    return this.summaryData?.count_by_category?.[category] ?? 0;
  }

  isCategorySelected(category: string): boolean {
    const selected = this.filterForm.get('category')?.value;
    return Array.isArray(selected) && selected.includes(category);
  }

  onCategoryCardClick(category: string): void {
    const selected: string[] = Array.isArray(this.filterForm.get('category')?.value)
      ? this.filterForm.get('category')?.value
      : [];
    // Clicking a card toggles that category in the (multi-select) filter, so
    // clicking it again returns to the unfiltered view.
    const next = selected.includes(category)
      ? selected.filter((c) => c !== category)
      : [...selected, category];
    this.filterForm.patchValue({ category: next });
    this.onFilterChange();
  }

  pageChange(page: number): void {
    if (this.page === page) return;
    this.page = page;
    this.loadReport();
  }

  getSummaryKeys(obj: { [key: string]: number } | undefined): string[] {
    return obj ? Object.keys(obj) : [];
  }

  getSummaryEntries(obj: { [key: string]: number } | undefined): { key: string; value: number }[] {
    return obj ? Object.entries(obj).map(([key, value]) => ({ key, value })) : [];
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      'Work in Progress': 'badge-info',
      'Pending': 'badge-warning',
      'Approved': 'badge-success',
      'Archived': 'badge-secondary',
      'Enquiry': 'badge-primary'
    };
    return map[status] || 'badge-secondary';
  }

  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;
    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';
    const query = this.buildFilterQuery();
    this.httpService.downloadPropertiesReportAll(fileType, query).subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;
    this.checkJobStatus(jobId);
    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => (Date.now() - startTime) < maxPollingTime && this.isDownloading)
    );
    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email' ? formValue.email : formValue.whatsapp;
    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(
        `Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`,
        'toast-top-right'
      );
      return;
    }
    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';
    const query = this.buildFilterQuery();
    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };
    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }
    const fileType = formValue.file_type || 'pdf';
    this.httpService.downloadPropertiesReportAll(fileType, query, shareData).subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;
    this.checkJobStatusForShare(jobId);
    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => (Date.now() - startTime) < maxPollingTime && this.isDownloading)
    );
    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;
          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {}
    });
  }

  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  ngOnDestroy(): void {
    this.reportSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
    this.searchDebounceSubscription?.unsubscribe();
  }
}
